import { Button } from "@/components/ui/button";
import { PUBLISH_STEP_STATE_LABEL } from "@/lib/tina/festival-page";
import type {
  FestivalPageRead,
  PublishStepRead,
} from "@/types/tina-festival-page";

interface PublishProgressProps {
  page: FestivalPageRead;
  busy: boolean;
  onRetry: () => void;
}

/** What one step does, in the words of the days and events the page names. */
export function stepTitle(
  step: PublishStepRead,
  page: FestivalPageRead,
): string {
  const number = page.days.find((d) => d.day === step.day)?.day_number;
  const event = page.days.find((d) => d.day === step.day)?.name;
  switch (step.step) {
    case "make_day":
      return number === undefined
        ? `Make ${step.subject}`
        : `Make Day ${number}`;
    case "update_day":
      return `Update ${event ?? step.subject}`;
    case "group":
      return "Make the group";
    case "replace":
      return `Replace ${step.subject}`;
    case "not_part":
      return `Leave ${step.subject} out`;
    case "remove_day":
      return `Take ${event ?? step.subject} out`;
    case "rename_group":
      return "Rename the group";
    case "delete_group":
      return "Delete the group";
  }
}

/** Where the approval's steps stand, one row each, with Retry once a step has failed. */
export function PublishProgress({ page, busy, onRetry }: PublishProgressProps) {
  const { publish } = page;
  if (publish === null) return null;
  const heading =
    page.status === "publishing"
      ? `Publishing: ${publish.days_done} of ${publish.days_total} days made`
      : page.status_label;
  const failed = publish.steps.some((s) => s.state === "failed");

  return (
    <section className="flex flex-col gap-2 rounded-lg border p-4">
      <h2 className="text-lg font-semibold">{heading}</h2>
      <ul className="flex flex-col gap-1 text-sm">
        {publish.steps.map((step, index) => (
          <li
            key={`${step.step}-${step.subject}-${index}`}
            className="flex flex-col"
          >
            <span className="flex gap-2">
              <span>{stepTitle(step, page)}</span>
              <span className="text-muted-foreground">
                {PUBLISH_STEP_STATE_LABEL[step.state]}
              </span>
            </span>
            {step.error ? (
              <span className="text-xs text-red-700">{step.error}</span>
            ) : null}
            {step.state === "needs_you" ? (
              <span className="text-xs text-amber-700">
                Something already in Mood needs you: see Already in Mood.
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {failed ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          disabled={busy}
          onClick={onRetry}
        >
          Retry
        </Button>
      ) : null}
    </section>
  );
}
